import React, { useState, useEffect } from 'react'
import {
  QrCodeIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  PlusIcon
} from '@heroicons/react/24/outline'

interface PIXKey {
  id: string
  key: string
  type: 'cpf' | 'cnpj' | 'email' | 'phone' | 'random'
  status: 'active' | 'pending' | 'inactive'
  created_at: string
  bank: string
}

interface PIXPayment {
  id: string
  amount: number
  description: string
  pix_key: string
  qr_code: string
  status: 'pending' | 'paid' | 'expired' | 'cancelled'
  customer_name?: string
  customer_email?: string
  created_at: string
  expires_at: string
  paid_at?: string
  transaction_id?: string
}

interface PIXStats {
  total_received_today: number
  total_received_month: number
  pending_payments: number
  completed_payments: number
  average_time_to_pay: number // in minutes
  total_keys: number
}

const PIXManager: React.FC = () => {
  const [keys, setKeys] = useState<PIXKey[]>([])
  const [payments, setPayments] = useState<PIXPayment[]>([])
  const [stats, setStats] = useState<PIXStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreatePayment, setShowCreatePayment] = useState(false)
  const [showCreateKey, setShowCreateKey] = useState(false)

  useEffect(() => {
    const fetchPIXData = async () => {
      try {
        setLoading(true)
        // Temporarily use mock data
        setKeys(mockKeys)
        setPayments(mockPayments)
        setStats(mockStats)
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar dados PIX')
      } finally {
        setLoading(false)
      }
    }

    fetchPIXData()
  }, [])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'paid': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'inactive':
      case 'expired':
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getKeyTypeLabel = (type: string) => {
    switch (type) {
      case 'cpf': return 'CPF'
      case 'cnpj': return 'CNPJ'
      case 'email': return 'E-mail'
      case 'phone': return 'Telefone'
      case 'random': return 'Chave Aleatória'
      default: return type
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // Add notification here
  }

  // Mock data
  const mockStats: PIXStats = {
    total_received_today: 12450.00,
    total_received_month: 485600.75,
    pending_payments: 8,
    completed_payments: 156,
    average_time_to_pay: 3.2,
    total_keys: 4
  }

  const mockKeys: PIXKey[] = [
    {
      id: '1',
      key: '12345678901',
      type: 'cpf',
      status: 'active',
      created_at: '2025-09-01T10:00:00Z',
      bank: 'Banco do Brasil'
    },
    {
      id: '2',
      key: 'contato@finpro.com',
      type: 'email',
      status: 'active',
      created_at: '2025-09-01T10:05:00Z',
      bank: 'Itaú'
    },
    {
      id: '3',
      key: '+5511999999999',
      type: 'phone',
      status: 'active',
      created_at: '2025-09-01T10:10:00Z',
      bank: 'Nubank'
    },
    {
      id: '4',
      key: '1234abcd-5678-efgh-9012-ijklmnopqrst',
      type: 'random',
      status: 'pending',
      created_at: '2025-09-21T15:00:00Z',
      bank: 'Inter'
    }
  ]

  const mockPayments: PIXPayment[] = [
    {
      id: '1',
      amount: 1250.00,
      description: 'Pagamento Serviços Consultoria',
      pix_key: 'contato@finpro.com',
      qr_code: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...',
      status: 'paid',
      customer_name: 'João Silva',
      customer_email: 'joao@email.com',
      created_at: '2025-09-21T14:00:00Z',
      expires_at: '2025-09-21T18:00:00Z',
      paid_at: '2025-09-21T14:15:00Z',
      transaction_id: 'pix_123456'
    },
    {
      id: '2',
      amount: 890.50,
      description: 'Compra Produto Digital',
      pix_key: '12345678901',
      qr_code: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...',
      status: 'pending',
      customer_name: 'Maria Santos',
      customer_email: 'maria@email.com',
      created_at: '2025-09-21T15:30:00Z',
      expires_at: '2025-09-21T19:30:00Z'
    },
    {
      id: '3',
      amount: 450.75,
      description: 'Assinatura Mensal',
      pix_key: '+5511999999999',
      qr_code: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...',
      status: 'expired',
      customer_name: 'Carlos Oliveira',
      customer_email: 'carlos@email.com',
      created_at: '2025-09-20T10:00:00Z',
      expires_at: '2025-09-20T14:00:00Z'
    }
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar PIX</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <QrCodeIcon className="w-7 h-7 text-blue-600" />
            Gestão PIX
          </h1>
          <p className="text-gray-600">Chaves PIX, pagamentos instantâneos e QR Codes</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreateKey(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            Nova Chave
          </button>
          <button
            onClick={() => setShowCreatePayment(true)}
            className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-green-700 hover:to-blue-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <QrCodeIcon className="w-5 h-5" />
            Gerar Cobrança
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Recebido Hoje</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_received_today)}</p>
              </div>
              <CurrencyDollarIcon className="w-8 h-8 text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Recebido no Mês</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.total_received_month)}</p>
              </div>
              <CalendarIcon className="w-8 h-8 text-blue-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Pagamentos Pendentes</p>
                <p className="text-2xl font-bold">{stats.pending_payments}</p>
              </div>
              <ClockIcon className="w-8 h-8 text-purple-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Tempo Médio</p>
                <p className="text-2xl font-bold">{stats.average_time_to_pay.toFixed(1)}min</p>
              </div>
              <ArrowPathIcon className="w-8 h-8 text-orange-200" />
            </div>
          </div>
        </div>
      )}

      {/* PIX Keys */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Minhas Chaves PIX</h3>
        </div>

        <div className="divide-y divide-gray-200">
          {keys.map((key) => (
            <div key={key.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <QrCodeIcon className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{key.key}</div>
                    <div className="text-sm text-gray-500">
                      {getKeyTypeLabel(key.type)} • {key.bank}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(key.status)}`}>
                    {key.status === 'active' ? 'Ativo' : key.status === 'pending' ? 'Pendente' : 'Inativo'}
                  </span>
                  <button
                    onClick={() => copyToClipboard(key.key)}
                    className="text-blue-600 hover:text-blue-900 p-1"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent PIX Payments */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Cobranças PIX Recentes</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{payment.customer_name}</div>
                        <div className="text-sm text-gray-500">{payment.customer_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{payment.description}</div>
                    <div className="text-sm text-gray-500">Chave: {payment.pix_key}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(payment.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                      {payment.status === 'paid' ? 'Pago' :
                       payment.status === 'pending' ? 'Pendente' :
                       payment.status === 'expired' ? 'Expirado' : 'Cancelado'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button className="text-blue-600 hover:text-blue-900 p-1">
                        <QrCodeIcon className="w-4 h-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-900 p-1">
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Payment Modal */}
      {showCreatePayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Gerar Cobrança PIX</h3>
            <p className="text-gray-600 mb-4">Interface de geração de cobrança será implementada...</p>
            <button
              onClick={() => setShowCreatePayment(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Create Key Modal */}
      {showCreateKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Cadastrar Nova Chave PIX</h3>
            <p className="text-gray-600 mb-4">Interface de cadastro de chave será implementada...</p>
            <button
              onClick={() => setShowCreateKey(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PIXManager